/**
 * Diner-facing copy for Chowly FPO error codes — TEAM-13882. See `resolveDinerMessage`
 * (`@/utils/fpoErrors`) for how a code becomes the sentence a diner reads, and
 * docs/DINER_ERROR_MESSAGES.md for why copy is keyed by code rather than audited string by string.
 *
 * Two rules that stop you doing the wrong thing:
 *
 * 1. **Copy lives here, in code — not in the per-brand Strings config.** Failure copy should not
 *    vary by brand: support recognises the message a customer describes, QA verifies one string, and
 *    no operator can author their own payment-failure wording. Where the restaurant name is needed,
 *    `interpolateIntoString`'s `$brandName$` token covers it without per-brand records. Do **not**
 *    author into `packages/sdk/src/types/gen/strings.fallback.ts` — it is generated, carries a
 *    DO-NOT-MODIFY banner, and hand edits are wiped by the next `yarn schema:sync`.
 * 2. **There is deliberately no same-PR sync obligation with coo-api.** `FpoErrorCode` mirrors
 *    coo-api's `ChowlyFPOErrorCode` (`core/Ordering/Engines/Chowly/Utility/ChowlyFPOErrorCode.php`),
 *    but a code coo-api adds later that is missing here falls back to tier 3/4/5 copy and emits one
 *    `sentryLogger.warn` — so drift is safe, self-reporting and volume-ranked rather than
 *    customer-facing. That warning only works because every code below is accounted for.
 *
 * @see docs/DINER_ERROR_MESSAGES.md
 */

/**
 * Numeric Chowly FPO error codes, as they arrive on the wire: numeric **strings**, because
 * `ChowlyFPOError::parseError` normalises them to the backing value of a `string`-backed PHP enum.
 */
export const FPO_ERROR_CODE = {
  Unknown: '0',
  MissingPlatform: '1',
  TemporarilyDisabled: '2',
  ItemSuspended: '3',
  MissingItem: '4',
  PriceChanged: '5',
  OutOfHours: '6',
  RequirementsNotMet: '7',
  BadServiceType: '8',
  MalformedPayload: '9',
  FailedDelivery: '10',
  PaymentError: '11',
  OrderCreateFailure: '12',
  TaxCalculationFailure: '13',
  ExpiredDeliveryQuote: '14',
  SubtotalTooLowForDelivery: '15',
  SubtotalTooLowForPickup: '16',
  BadRequest: '17',
  Unauthorized: '18',
  RequestTimeout: '19',
  RateLimit: '20',
  Dependency: '21',
  RecordNotUnique: '22',
  InvalidDiscountCode: '23',
  GiftCardError: '24',
  InvalidReward: '25',
  OrderTimeNotAvailable: '26',
  SubtotalTooHighForDelivery: '27',
  SubtotalTooHighForPickup: '28',
  NoMatchingRewardItem: '29',
  DeliveryQuoteUnavailable: '30',
  ToastPaymentIntentError: '31',
  /** chowly-api calls this `BreadcrumbTicketError`. */
  RestaurantTemporarilyUnavailable: '32',
  InvalidGiftCard: '33',
  InvalidGiftCardPin: '34',
  InsufficientGiftCardBalance: '35',
  EmptyCart: '36',
} as const;

/** A code **this catalog knows**. Every one of these has an `FPO_ERROR_COPY` entry. */
export type FpoErrorCode = (typeof FPO_ERROR_CODE)[keyof typeof FPO_ERROR_CODE];

/**
 * A numeric code **as it arrived on the wire** — catalogued or not.
 *
 * Deliberately a different type from `FpoErrorCode`. coo-api can send a code this catalog has never
 * heard of (that is what the drift detector exists to report), so a value that has only been
 * shape-checked must not be typed as a catalog key: `FPO_ERROR_COPY` is a `Record<FpoErrorCode, …>`
 * and would then claim a defined entry for a code that has none. `asWireFpoErrorCode`
 * (`@/utils/fpoErrors`) is the only producer, and `lookupFpoErrorCopy` below is the only way to get
 * from one of these to catalog copy.
 *
 * The template literal is the closest type-level statement of "numeric string"; the runtime
 * `/^\d+$/` check in `asWireFpoErrorCode` is the actual guarantee.
 */
export type WireFpoErrorCode = `${number}`;

/** Tier 5 of the render rule: nothing else resolved, and we still say nothing technical. */
export const GENERIC_ERROR_COPY = 'Something went wrong. Please try again.';

/**
 * Tier 1 of the render rule. Every one of the 37 codes is accounted for; `null` means a dedicated
 * UI already tells the diner what happened, so adding a sentence here would double-message.
 *
 * Constraints, enforced by `utils/__tests__/fpoErrorCopy.ts`:
 * - no `<` or `&` — these strings can reach the global toast, which renders with
 *   `dangerouslySetInnerHTML`
 * - no "not taking orders" / "store_is_closed" / "store is closed" — coo-api's
 *   `Handler::isRestaurantTemporarilyUnavailable` substring-matches exception messages and would
 *   rewrite the response into the restaurant-unavailable error
 * - under `FPO_ERROR_COPY_MAX_LENGTH`, so a toast stays readable on a phone
 */
export const FPO_ERROR_COPY: Record<FpoErrorCode, string | null> = {
  [FPO_ERROR_CODE.Unknown]: 'Something went wrong on our end. Please try again.',
  [FPO_ERROR_CODE.MissingPlatform]:
    "We couldn't reach this restaurant's ordering system. Please try again in a few minutes.",
  [FPO_ERROR_CODE.TemporarilyDisabled]:
    "This restaurant isn't accepting online orders right now. Please try again later.",
  [FPO_ERROR_CODE.ItemSuspended]:
    'One of the items in your order is no longer available. Please remove it and try again.',
  [FPO_ERROR_CODE.MissingItem]:
    'One of the items in your order is no longer on the menu. Please remove it and try again.',
  [FPO_ERROR_CODE.PriceChanged]:
    'Prices for one or more items in your order have changed. Please review your order and try again.',
  [FPO_ERROR_CODE.OutOfHours]:
    "One of the items in your order isn't served at the time you chose. Please pick a different time or remove the item.",
  [FPO_ERROR_CODE.RequirementsNotMet]:
    'One of the items in your order is missing a required choice. Please review your order and try again.',
  [FPO_ERROR_CODE.BadServiceType]:
    "This restaurant doesn't offer that order type right now. Please choose pickup or delivery again.",
  [FPO_ERROR_CODE.MalformedPayload]:
    "We couldn't send your order to the restaurant. Please refresh the page and try again.",
  [FPO_ERROR_CODE.FailedDelivery]:
    "We couldn't arrange delivery for this order. Please try again, or switch to pickup.",
  [FPO_ERROR_CODE.PaymentError]:
    "We couldn't process your payment. Please check your payment details and try again.",
  [FPO_ERROR_CODE.OrderCreateFailure]:
    "The restaurant couldn't accept your order. Please try again in a few minutes.",
  /**
   * Dedicated UI (TEAM-14493): `isPricingUnavailableError` (`@/utils/errors`) routes this to the
   * terminal pricing-unavailable screen, which tells the diner to call the restaurant. chowly-api
   * now sends this code for every Toast pricing failure, not just tax, and no retry can succeed —
   * so the old "calculate the tax … try again" sentence was wrong on both counts, and any copy here
   * would appear alongside that screen.
   */
  [FPO_ERROR_CODE.TaxCalculationFailure]: null,
  [FPO_ERROR_CODE.ExpiredDeliveryQuote]:
    'Your delivery quote expired. Please refresh your order for an updated delivery time and fee.',
  [FPO_ERROR_CODE.SubtotalTooLowForDelivery]:
    'Your order is below the minimum for delivery. Please add a little more, or switch to pickup.',
  [FPO_ERROR_CODE.SubtotalTooLowForPickup]:
    'Your order is below the minimum for pickup. Please add a little more before checking out.',
  [FPO_ERROR_CODE.BadRequest]:
    "Something in your order wasn't accepted. Please refresh the page and try again.",
  [FPO_ERROR_CODE.Unauthorized]: 'Your session expired. Please sign in again to finish your order.',
  [FPO_ERROR_CODE.RequestTimeout]: 'The restaurant took too long to respond. Please try again.',
  [FPO_ERROR_CODE.RateLimit]:
    "We're getting a lot of requests right now. Please wait a moment and try again.",
  [FPO_ERROR_CODE.Dependency]:
    "We're having trouble reaching the restaurant. Please try again in a few minutes.",
  [FPO_ERROR_CODE.RecordNotUnique]:
    'It looks like this order was already started. Please refresh the page and try again.',
  [FPO_ERROR_CODE.InvalidDiscountCode]: "That promo code isn't valid for this order.",
  [FPO_ERROR_CODE.GiftCardError]:
    "We couldn't apply that gift card. Please check the details and try again.",
  [FPO_ERROR_CODE.InvalidReward]: "That reward can't be applied to this order.",
  [FPO_ERROR_CODE.OrderTimeNotAvailable]:
    'The time you chose is no longer available. Please pick a different time.',
  [FPO_ERROR_CODE.SubtotalTooHighForDelivery]:
    'Your order is above the maximum for delivery. Please remove a few items, or switch to pickup.',
  [FPO_ERROR_CODE.SubtotalTooHighForPickup]:
    'Your order is above the maximum this restaurant can accept. Please remove a few items and try again.',
  [FPO_ERROR_CODE.NoMatchingRewardItem]:
    "Your order doesn't include an item that qualifies for this reward.",
  [FPO_ERROR_CODE.DeliveryQuoteUnavailable]:
    "We couldn't get a delivery quote for this address. Please try a different address, or switch to pickup.",
  [FPO_ERROR_CODE.ToastPaymentIntentError]:
    "We couldn't process your payment. Please check your payment details and try again.",
  /**
   * Dedicated UI: `isRestaurantUnavailableError` (`@/utils/errors`) routes this to the
   * service-unavailable screen. In `initializeOrderSaga` that screen and the reconciliation modal
   * are not mutually exclusive, so catalog copy here would appear alongside it.
   */
  [FPO_ERROR_CODE.RestaurantTemporarilyUnavailable]: null,
  [FPO_ERROR_CODE.InvalidGiftCard]:
    "We couldn't find that gift card. Please check the number and try again.",
  [FPO_ERROR_CODE.InvalidGiftCardPin]:
    "That gift card PIN wasn't correct. Please check it and try again.",
  [FPO_ERROR_CODE.InsufficientGiftCardBalance]:
    "That gift card doesn't have enough left to cover your order. Please add another payment method.",
  [FPO_ERROR_CODE.EmptyCart]: 'Your cart is empty. Please add something before checking out.',
};

export const FPO_ERROR_COPY_MAX_LENGTH = 160;

/**
 * The **only** place `FPO_ERROR_COPY` is indexed, and the reason `WireFpoErrorCode` and
 * `FpoErrorCode` are separate types.
 *
 * Three outcomes, all meaningful to the render rule:
 * - a `string` — catalogued copy, show it
 * - `null` — catalogued as "a dedicated UI already says this", fall through silently
 * - `undefined` — not in the catalog at all, fall through *and* warn (the drift detector)
 *
 * Keeping `FPO_ERROR_COPY` typed as a total `Record<FpoErrorCode, …>` is what makes authoring
 * exhaustive, so the widening happens here, on the read, rather than on the catalog.
 */
export function lookupFpoErrorCopy(code: WireFpoErrorCode): string | null | undefined {
  return (FPO_ERROR_COPY as Record<string, string | null | undefined>)[code];
}
